/**
 * Step to log into an existing account for Clever Library users.
 */
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {useGql} from "@khan/data-gql";
import {useLocation} from "@khan/urls";

import LinkAccountLoginForm from "./link-account-login-form.tsx";
import {
    completePostLogin,
    linkClever,
    loginCleverUser,
} from "./login-actions.ts";

interface Props {
    defaultIdentifier?: string;
    isTeacher: boolean;
    transferToken?: string;
    onForgotPasswordClick: () => void;
    onStudentLogin: () => void;
    onLoginSubmit: () => void;
    onLoginError: () => void;
    titleId?: string;
}

export default function LoginStep({
    defaultIdentifier,
    isTeacher,
    transferToken,
    onForgotPasswordClick,
    onStudentLogin,
    onLoginSubmit,
    onLoginError,
    titleId,
}: Props) {
    const gqlFetch = useGql();
    const location = useLocation();

    const submitLogin = React.useCallback(
        (identifier: string, password: string) => {
            onLoginSubmit();

            // Error handling is done by the LinkAccountLoginForm
            return loginCleverUser(gqlFetch, identifier, password).then(
                (loginData) => {
                    if (isTeacher) {
                        if (transferToken) {
                            return linkClever(gqlFetch, {transferToken}).then(
                                () => {
                                    completePostLogin(
                                        gqlFetch,
                                        isTeacher,
                                        "/",
                                        loginData,
                                        location,
                                    );
                                },
                            );
                        }
                        completePostLogin(
                            gqlFetch,
                            isTeacher,
                            "/",
                            loginData,
                            location,
                        );
                    } else {
                        onStudentLogin();
                    }
                },
            );
        },
        [
            gqlFetch,
            isTeacher,
            transferToken,
            location,
            onStudentLogin,
            onLoginSubmit,
        ],
    );

    return (
        <View>
            <Strut size={spacing.medium_16} />
            <LinkAccountLoginForm
                titleId={titleId}
                isTeacher={isTeacher}
                isCleverLibrary={true}
                cleverToken={transferToken}
                defaultIdentifier={defaultIdentifier}
                continueUrl="/"
                onForgotPasswordClick={onForgotPasswordClick}
                handleLogin={submitLogin}
                onLoginError={onLoginError}
            />
        </View>
    );
}
